import * as React from "react";
import {
  Box,
  FormControl,
  FormLabel,
  FormHelperText,
  Autocomplete as MuiAutocomplete,
  TextField,
} from "@mui/material";
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useMemo, memo, useCallback } from 'react';

interface AutocompleteProps {
  label: string;
  description?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: string[];
  required?: boolean;
  error?: string;
  enabled?: boolean;
  visible?: boolean;
  appValidated?: boolean;
}

const Autocomplete = memo(({
                             label,
                             description,
                             value,
                             onChange,
                             options,
                             error,
                             required = false,
                             enabled = true,
                             visible = true,
                             appValidated = false
                           }: AutocompleteProps) => {

  const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

  React.useEffect(() => {
    if (appValidated) {
        setAppHasBeenValidated(true);
    }
  }, [appValidated]);

  const showRequiredError = useMemo(() =>
          required && appHasBeenValidated && !value,
      [required, appHasBeenValidated, value]
  );

  const showError = useMemo(() => {
    if (!error) return false;

    if (error.includes('must be string') || error.includes('must be equal to one of the allowed values')) {
      return false;
    }
    return true;
  }, [error]);


  const hasError = showError || showRequiredError;
  const errorMessage = showRequiredError ? 'This field is required' : error;

  // Memoize computed values
  const showDescription = useMemo(() =>
          description !== undefined && description !== "",
      [description]
  );

  // Memoize filter creation (createFilterOptions is stable, but this ensures it)
  const filter = useMemo(() => createFilterOptions<string>(), []);

  // Memoize searchPrefix since it's constant
  const searchPrefix = useMemo(() => "", []);

  // Memoize the onChange handler
  const handleChange = useCallback((event: any, newValue: string | null) => {
    onChange(newValue !== null ? newValue.replace(searchPrefix, "") : null);
  }, [onChange, searchPrefix]);

  // Memoize filterOptions function
  const filterOptions = useCallback((options: string[], params: any) => {
    const filtered = filter(options, params);

    const { inputValue } = params;
    // Suggest the creation of a new value
    const isExisting = options.some((option) => inputValue === option);
    if (inputValue !== '' && !isExisting) {
      filtered.push(searchPrefix + inputValue);
    }

    return filtered;
  }, [filter, searchPrefix]);
  if (!visible) {
    return null;
  }

  return (
      <Box sx={{ display: "block" }}>
        <FormControl className="form-control" fullWidth>
          <FormLabel
              required={required}
              color="secondary"
              className="form-separate-label"
          >
            {label}
          </FormLabel>
          <MuiAutocomplete
              freeSolo
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              options={options}
              value={value}
              onChange={handleChange}
              filterOptions={filterOptions}
              disabled={!enabled}
              renderInput={(params) => (
                  <TextField
                      {...params}
                      error={hasError}
                      required={required}
                      variant="outlined"
                      className="form-control-field"
                  />
              )}
          />
          {showDescription && (
              <FormHelperText>{description}</FormHelperText>
          )}
          {hasError && (
              <FormHelperText error={true}>
                {errorMessage}
              </FormHelperText>
          )}
        </FormControl>
      </Box>
  );
});

Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;
